import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { 
  Search, 
  Eye, 
  UserCheck, 
  Clock, 
  AlertCircle, 
  CheckCircle2, 
  FileText,
  Mail,
  Phone,
  Building2,
  Calendar,
  Download,
  Send,
  CheckCircle,
  MessageCircle,
  Trash2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('list');
  const [newComment, setNewComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching support tickets...');
      const response = await api.getSupportTickets();
      console.log('Support tickets response:', response.data);
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast({
        title: "Error loading tickets",
        description: "Unable to fetch support tickets",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const assignTicket = async (ticketId) => {
    try {
      await api.assignSupportTicket(ticketId);
      toast({
        title: "Ticket assigned",
        description: "Support ticket has been assigned to you",
      });
      fetchTickets(); // Refresh the list
      if (selectedTicket && selectedTicket.id === ticketId) {
        // Refresh selected ticket details
        const response = await api.getSupportTicket(ticketId);
        setSelectedTicket(response.data);
      }
    } catch (error) {
      console.error('Error assigning ticket:', error);
      toast({
        title: "Assignment failed",
        description: error.response?.data?.detail || "Unable to assign ticket",
        variant: "destructive",
      });
    }
  };

  const viewTicketDetails = async (ticket) => {
    try {
      const response = await api.getSupportTicket(ticket.id);
      setSelectedTicket(response.data);
      setActiveTab('details');
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      toast({
        title: "Error loading ticket details",
        description: "Unable to fetch ticket details",
        variant: "destructive",
      });
    }
  };

  const addComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;
    
    try {
      setIsAddingComment(true);
      await api.addTicketComment(selectedTicket.id, newComment.trim());
      toast({
        title: "Comment added",
        description: "Your comment has been sent to the client",
      });
      setNewComment('');
      
      // Refresh ticket details
      const response = await api.getSupportTicket(selectedTicket.id);
      setSelectedTicket(response.data);
      fetchTickets(); // Update the main list
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        title: "Comment failed",
        description: error.response?.data?.detail || "Unable to add comment",
        variant: "destructive",
      });
    } finally {
      setIsAddingComment(false);
    }
  };

  const updateTicketStatus = async (status) => {
    if (!selectedTicket) return;
    
    try {
      setIsUpdatingStatus(true);
      await api.updateTicketStatus(selectedTicket.id, status);
      toast({
        title: "Status updated",
        description: `Ticket has been marked as ${status.replace('_', ' ').toLowerCase()}`,
      });
      
      // Refresh ticket details
      const response = await api.getSupportTicket(selectedTicket.id);
      setSelectedTicket(response.data);
      fetchTickets(); // Update the main list
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Status update failed",
        description: error.response?.data?.detail || "Unable to update status",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const downloadAttachment = async (filename) => {
    if (!selectedTicket) return;
    
    try {
      const response = await api.downloadAttachment(selectedTicket.id, filename);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename.split('_').slice(1).join('_'); // Remove UUID prefix
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast({
          title: "Download started",
          description: `Downloading ${filename}`,
        });
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast({
        title: "Download failed",
        description: "Unable to download attachment",
        variant: "destructive",
      });
    }
  };

  const deleteAllTickets = async () => {
    if (!window.confirm('Are you sure you want to delete ALL support tickets? This action cannot be undone.')) {
      return;
    }
    
    if (!window.confirm('This will permanently delete all support tickets and their attachments. Are you absolutely sure?')) {
      return;
    }
    
    try {
      setIsDeletingAll(true);
      const response = await api.deleteAllSupportTickets();
      toast({
        title: "All tickets deleted",
        description: `Successfully deleted ${response.data.deleted_count} support tickets`,
      });
      
      // Reset state
      setTickets([]);
      setSelectedTicket(null);
      setActiveTab('list');
      
    } catch (error) {
      console.error('Error deleting all tickets:', error);
      toast({
        title: "Delete failed",
        description: error.response?.data?.detail || "Unable to delete all tickets",
        variant: "destructive",
      });
    } finally {
      setIsDeletingAll(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500';
      case 'assigned': return 'bg-yellow-500';
      case 'in_progress': return 'bg-orange-500';
      case 'resolved': return 'bg-green-500';
      case 'closed': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getUrgencyIcon = (urgency) => {
    switch (urgency) {
      case 'low': return <CheckCircle2 className="h-4 w-4" />;
      case 'medium': return <Clock className="h-4 w-4" />;
      case 'high': return <AlertCircle className="h-4 w-4" />;
      case 'critical': return <AlertCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    const matchesSearch = ticket.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.issue_description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const newTicketsCount = tickets.filter(t => t.status === 'new').length;
  const assignedToMeCount = tickets.filter(t => t.assigned_user_id === user?.id).length;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Support Tickets</h1>
        <p className="text-gray-600">Manage and respond to client support requests</p>
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg mr-3">
                  <AlertCircle className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">New Tickets</p>
                  <p className="text-2xl font-bold text-gray-900">{newTicketsCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg mr-3">
                  <UserCheck className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Assigned to Me</p>
                  <p className="text-2xl font-bold text-gray-900">{assignedToMeCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <div className="p-2 bg-gray-100 rounded-lg mr-3">
                  <FileText className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Tickets</p>
                  <p className="text-2xl font-bold text-gray-900">{tickets.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="list">Ticket List</TabsTrigger>
          <TabsTrigger value="details" disabled={!selectedTicket}>Ticket Details</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle>Support Tickets</CardTitle>
                
                {/* Admin Controls */}
                {user?.role === 'admin' && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={deleteAllTickets}
                    disabled={isDeletingAll || tickets.length === 0}
                  >
                    {isDeletingAll ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete All Tickets
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              {/* Filters */}
              <div className="flex gap-4 items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search tickets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="assigned">Assigned</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading tickets...</p>
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No support tickets found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredTickets.map((ticket) => (
                    <div key={ticket.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={`${getStatusColor(ticket.status)} text-white`}>
                              {ticket.status.replace('_', ' ').toUpperCase()}
                            </Badge>
                            <Badge variant="outline" className={getUrgencyColor(ticket.urgency)}>
                              {getUrgencyIcon(ticket.urgency)}
                              <span className="ml-1">{ticket.urgency.toUpperCase()}</span>
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h3 className="font-semibold text-gray-900">{ticket.client_name}</h3>
                              <p className="text-sm text-gray-600 flex items-center mt-1">
                                <Building2 className="h-4 w-4 mr-1" />
                                {ticket.business_name}
                              </p>
                              <p className="text-sm text-gray-600 flex items-center mt-1">
                                <Mail className="h-4 w-4 mr-1" />
                                {ticket.email}
                              </p>
                            </div>
                            
                            <div>
                              <p className="text-sm text-gray-900 font-medium">Issue:</p>
                              <p className="text-sm text-gray-600 line-clamp-2">
                                {ticket.issue_description}
                              </p>
                              <p className="text-xs text-gray-500 flex items-center mt-2">
                                <Calendar className="h-3 w-3 mr-1" />
                                {new Date(ticket.created_date).toLocaleDateString()} at{' '}
                                {new Date(ticket.created_date).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>

                          {ticket.assigned_user_name && (
                            <div className="mt-2">
                              <Badge variant="secondary">
                                <UserCheck className="h-3 w-3 mr-1" />
                                Assigned to {ticket.assigned_user_name}
                              </Badge>
                            </div>
                          )}

                          {ticket.attachments && ticket.attachments.length > 0 && (
                            <div className="mt-2">
                              <Badge variant="outline">
                                <FileText className="h-3 w-3 mr-1" />
                                {ticket.attachments.length} attachment(s)
                              </Badge>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewTicketDetails(ticket)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          
                          {!ticket.assigned_user_id && (
                            <Button
                              size="sm"
                              onClick={() => assignTicket(ticket.id)}
                            >
                              <UserCheck className="h-4 w-4 mr-1" />
                              Take Job
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="details">
          {selectedTicket ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Ticket Details</CardTitle>
                  <div className="flex gap-2">
                    <Badge className={`${getStatusColor(selectedTicket.status)} text-white`}>
                      {selectedTicket.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <Badge variant="outline" className={getUrgencyColor(selectedTicket.urgency)}>
                      {getUrgencyIcon(selectedTicket.urgency)}
                      <span className="ml-1">{selectedTicket.urgency.toUpperCase()}</span>
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Client Information</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Client Name</label>
                        <p className="text-gray-900">{selectedTicket.client_name}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Business Name</label>
                        <p className="text-gray-900">{selectedTicket.business_name}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Email Address</label>
                        <p className="text-gray-900">{selectedTicket.email}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Mobile Phone</label>
                        <p className="text-gray-900">{selectedTicket.mobile_phone}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Ticket Information</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Created Date</label>
                        <p className="text-gray-900">
                          {new Date(selectedTicket.created_date).toLocaleDateString()} at{' '}
                          {new Date(selectedTicket.created_date).toLocaleTimeString()}
                        </p>
                      </div>
                      {selectedTicket.assigned_user_name && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Assigned To</label>
                          <p className="text-gray-900">{selectedTicket.assigned_user_name}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-gray-600">Ticket ID</label>
                        <p className="text-gray-900 font-mono text-sm">{selectedTicket.id}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Issue Description</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-900 whitespace-pre-wrap">{selectedTicket.issue_description}</p>
                  </div>
                </div>

                {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-gray-900 mb-2">Attachments</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {selectedTicket.attachments.map((attachment, index) => {
                        const filename = attachment.split('/').pop();
                        const displayName = filename.split('_').slice(1).join('_');
                        
                        return (
                          <div key={filename || index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex items-center flex-1">
                              <FileText className="h-5 w-5 text-gray-500 mr-2" />
                              <span className="text-sm text-gray-900 truncate" title={displayName}>
                                {displayName}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadAttachment(filename)}
                              className="ml-2"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Comments Section */}
                {selectedTicket.comments && selectedTicket.comments.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Communication History</h3>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {selectedTicket.comments.map((comment, index) => (
                        <div key={comment.id || `${comment.user_name}-${index}`} className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-blue-900">{comment.user_name}</span>
                            <span className="text-xs text-blue-600">
                              {new Date(comment.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-blue-800 whitespace-pre-wrap">{comment.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add Comment Section - Only if ticket is assigned to current user or user is admin */}
                {selectedTicket.assigned_user_id === user?.id || user?.role === 'admin' ? (
                  <div className="mt-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Add Comment</h3>
                    <div className="space-y-3">
                      <Textarea
                        placeholder="Type your message to the client..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <Button 
                          onClick={addComment}
                          disabled={!newComment.trim() || isAddingComment}
                        >
                          {isAddingComment ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              Send Comment
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="mt-6 space-y-4">
                  {/* Status Update Buttons - Only if ticket is assigned to current user or user is admin */}
                  {(selectedTicket.assigned_user_id === user?.id || user?.role === 'admin') && 
                   selectedTicket.status !== 'closed' && (
                    <div className="border-t pt-4">
                      <h3 className="font-semibold text-gray-900 mb-3">Update Status</h3>
                      <div className="flex gap-2 flex-wrap">
                        {(selectedTicket.status === 'new' || selectedTicket.status === 'assigned') && (
                          <Button 
                            variant="outline"
                            onClick={() => updateTicketStatus('in_progress')}
                            disabled={isUpdatingStatus}
                          >
                            <Clock className="h-4 w-4 mr-1" />
                            Start Working
                          </Button>
                        )}
                        
                        {selectedTicket.status === 'in_progress' && (
                          <Button 
                            variant="outline"
                            onClick={() => updateTicketStatus('resolved')}
                            disabled={isUpdatingStatus}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Mark Resolved
                          </Button>
                        )}
                        
                        {(selectedTicket.status === 'resolved' || selectedTicket.status === 'in_progress' || selectedTicket.status === 'assigned') && (
                          <Button 
                            onClick={() => updateTicketStatus('closed')}
                            disabled={isUpdatingStatus}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Close Ticket
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex gap-2 border-t pt-4">
                    <Button variant="outline" onClick={() => setActiveTab('list')}>
                      Back to List
                    </Button>
                    
                    {!selectedTicket.assigned_user_id && (
                      <Button onClick={() => assignTicket(selectedTicket.id)}>
                        <UserCheck className="h-4 w-4 mr-1" />
                        Take Job
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Select a ticket to view details</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SupportTickets;